import { format } from 'date-fns';
import { StateCreator } from 'zustand';
import { StorePerformanceInterval } from '../constants';
import { LocalStorageKey } from '../constants/localStorageKey';
import { createStore, StoreWithPersist } from './createStore';

export type ExchangeRates = { [key in string]?: number };

export const getExchangeRatesKey = (interval: StorePerformanceInterval, date: Date) => {
  return `${interval.toString()}_${format(date, 'yyyy-MM-dd')}`;
};

interface CurrencyStore {
  userCurrency: string | null;
  setUserCurrency: (currency: string) => void;

  exchangeRatesMap: Record<string, ExchangeRates | undefined>;
  addExchangeRates: (interval: StorePerformanceInterval, date: Date, rates: ExchangeRates) => void;
  getExchangeRates: (interval: StorePerformanceInterval, date: Date) => ExchangeRates | null;
  resetUserCurrency: () => void;

  latestExchangeRatesKey: string | null;
  setLatestExchangeRatesKey: (key: string | null) => void;
}

const baseStore: StateCreator<CurrencyStore> = (set, get) => {
  const initialUserCurrency = localStorage.getItem(LocalStorageKey.SelectedCurrency);

  return {
    userCurrency: initialUserCurrency,

    setUserCurrency: (currency: string) => {
      localStorage.setItem(LocalStorageKey.SelectedCurrency, currency);
      set({ userCurrency: currency });
    },

    exchangeRatesMap: {},
    latestExchangeRatesKey: null,

    addExchangeRates: (interval: StorePerformanceInterval, date: Date, rates: ExchangeRates) => {
      set((state) => {
        const { exchangeRatesMap } = state;

        const key = getExchangeRatesKey(interval, date);
        const updatedExchangeRatesMap = { ...exchangeRatesMap, [key]: rates };

        return { exchangeRatesMap: updatedExchangeRatesMap };
      });
    },

    setLatestExchangeRatesKey: (key: string | null) => {
      set({ latestExchangeRatesKey: key });
    },

    getExchangeRates: (interval: StorePerformanceInterval, date: Date) => {
      const { exchangeRatesMap } = get();
      const key = getExchangeRatesKey(interval, date);
      const exchangeRates = exchangeRatesMap[key];
      return exchangeRates || null;
    },
    resetUserCurrency: () => {
      localStorage.removeItem(LocalStorageKey.SelectedCurrency);
      set({ userCurrency: null, exchangeRatesMap: {}, latestExchangeRatesKey: null });
    },
  };
};

export const useCurrencyStore = createStore<CurrencyStore>(baseStore, {
  devtools: {
    name: 'CurrencyStore',
  },
}) as StoreWithPersist<CurrencyStore>;
